import React, { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { get } from 'lodash';
import { Info } from 'lucide-react';

import { OrderSchemaType } from '../order.schema';
import { Client, Seller, Supplier } from '../types';
import { FormGrid, FormGroup, Label, Select, Separator, InfoLink } from '../style';
import InfoTooltip from '../../../../../components/InfoTooltip';

interface MainInfosSectionProps {
  suppliers: Supplier[];
  clients: Client[];
  sellers: Seller[];
}

const MainInfosSection: React.FC<MainInfosSectionProps> = ({ suppliers, clients, sellers }) => {
  const { control, watch, formState: { errors }, setValue } = useFormContext<OrderSchemaType>();

  const [activeInfo, setActiveInfo] = useState<string | null>(null);

  const handleInfoToggle = (type: string, hasDetails: boolean) => {
    if (!hasDetails) return;
    setActiveInfo(activeInfo === type ? null : type);
  };

  const selectedSupplierName = watch('supplier');
  const selectedClientName = watch('client');
  const selectedSellerName = watch('seller');

  const selectedSupplier = suppliers.find(s => s.name === selectedSupplierName);
  const selectedClient = clients.find(c => c.name === selectedClientName);
  const selectedSeller = sellers.find(s => s.name === selectedSellerName);

  const handleSupplierChange = (supplierName: string) => {
    const supplier = suppliers.find(s => s.name === supplierName);
    setValue('supplier', supplierName);
    setValue('supplierId', supplier ? Number(supplier.id) : undefined);
  };

  const handleClientChange = (clientName: string) => {
    const client = clients.find(c => c.name === clientName);
    setValue('client', clientName);
    setValue('clientId', client ? Number(client.id) : undefined);
  };

  const handleSellerChange = (sellerName: string) => {
    const seller = sellers.find(s => s.name === sellerName);
    setValue('seller', sellerName);
    setValue('sellerId', seller ? Number(seller.id) : undefined);
  };

  return (
    <>
      <FormGrid>
        <FormGroup style={{ position: 'relative' }}>
          <Label htmlFor="supplier">Fornecedor</Label>
          <Controller
            name="supplier"
            control={control}
            render={({ field }) => (
              <Select
                id="supplier"
                {...field}
                hasError={!!get(errors, 'supplier')}
                onChange={(e) => {
                  field.onChange(e);
                  handleSupplierChange(e.target.value);
                  setActiveInfo(null);
                }}
              >
                <option value="">Selecione um fornecedor</option>
                {suppliers.map((s, index) => <option key={`supplier-${s.id || index}`} value={s.name}>{s.name}</option>)}
              </Select>
            )}
          />
          <InfoLink
            disabled={!selectedSupplier}
            onClick={() => handleInfoToggle('supplier', !!selectedSupplier)}
          >
            <Info size={16} />
            <span>Mais informações</span>
          </InfoLink>
          {activeInfo === 'supplier' && selectedSupplier && (
            <InfoTooltip details={Object.fromEntries(Object.entries(selectedSupplier).map(([k, v]) => [k, String(v ?? '')]))} />
          )}
        </FormGroup>

        <FormGroup style={{ position: 'relative' }}>
          <Label htmlFor="seller">Vendedor</Label>
          <Controller
            name="seller"
            control={control}
            render={({ field }) => (
              <Select
                id="seller"
                {...field}
                hasError={!!get(errors, 'seller')}
                onChange={(e) => {
                  field.onChange(e);
                  handleSellerChange(e.target.value);
                  setActiveInfo(null);
                }}
              >
                <option value="">Selecione um vendedor</option>
                {sellers.map((s, index) => <option key={`seller-${s.id || index}`} value={s.name}>{s.name}</option>)}
              </Select>
            )}
          />
          <InfoLink
            disabled={!selectedSeller}
            onClick={() => handleInfoToggle('seller', !!selectedSeller)}
          >
            <Info size={16} />
            <span>Mais informações</span>
          </InfoLink>
          {activeInfo === 'seller' && selectedSeller && (
            <InfoTooltip details={Object.fromEntries(Object.entries(selectedSeller).map(([k, v]) => [k, String(v ?? '')]))} />
          )}
        </FormGroup>

        <FormGroup style={{ position: 'relative' }}>
          <Label htmlFor="client">Cliente</Label>
          <Controller
            name="client"
            control={control}
            render={({ field }) => (
              <Select
                id="client"
                {...field}
                hasError={!!get(errors, 'client')}
                onChange={(e) => {
                  field.onChange(e);
                  handleClientChange(e.target.value);
                  setActiveInfo(null);
                }}
              >
                <option value="">Selecione um cliente</option>
                {clients.map((c, index) => <option key={`client-${c.id || index}`} value={c.name}>{c.name}</option>)}
              </Select>
            )}
          />
          <InfoLink
            disabled={!selectedClient}
            onClick={() => handleInfoToggle('client', !!selectedClient)}
          >
            <Info size={16} />
            <span>Mais informações</span>
          </InfoLink>
          {activeInfo === 'client' && selectedClient && (
            <InfoTooltip details={Object.fromEntries(Object.entries(selectedClient).map(([k, v]) => [k, String(v ?? '')]))} />
          )}
        </FormGroup>
      </FormGrid>
      <Separator />
    </>
  );
};

export default MainInfosSection;